import {
    CheckboxFormFieldComponent,
    DatePickerFormFieldComponent,
    TextAreaFormFieldComponent,
    TextFormFieldComponent
} from '@vivli/shared/components';
import { ChangeEvent, CSSProperties, useEffect } from 'react';
import { Size } from '@vivli/shared/theme';
import { useNotificationContext } from '@vivli/shared/features/notification/infrastructure/context';
import { useNotificationForm } from '@vivli/shared/features/notification/infrastructure/hook';
import { FormProvider } from 'react-hook-form';
import moment from 'moment';

const vivliPrivateFieldStyle: CSSProperties = {
    backgroundColor: 'rgb(255, 246, 217)'
};

const mainContainerStyle: CSSProperties = {
    paddingLeft: Size.INSET_SIZE,
    paddingRight: Size.INSET_SIZE,
    paddingBottom: Size.INSET_SIZE
};

const formContainerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: '20px'
};

const buttonContainerStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
};
const textStyle: CSSProperties = {
    margin: '20px',
    display: 'flex'
};
const dateText: CSSProperties = {
    marginLeft: '10px',
    fontWeight: 600
};
const rightSectionStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px'
};
export const NotificationFormComponent = ({ notification }) => {
    const { firstAndLastDates, lastSentMessageState, setFormApi } = useNotificationContext();
    const formApi = useNotificationForm(notification);

    useEffect(() => {
        setFormApi(formApi);
    }, []);

    useEffect(() => {
        if (!notification) return;

        formApi.reset(notification);
    }, [notification]);

    // Workaround: certain shared field components (checkbox, date picker) don't reliably
    // forward their value changes to RHF's dirty tracking. Calling setValue explicitly with
    // shouldDirty/shouldValidate guarantees the Save button reactivates per RQ-113.
    // Note that this workaround is to make a careful, isolated change due to closeness of 4.0
    // code freeze.
    // TODO - generalize to the base form components so this workaround won't be necessary.
    const setFieldDirty = (name: string, value: unknown) =>
        formApi.setValue(name as any, value, { shouldDirty: true, shouldValidate: true });

    // TODO - generalize to the DatePicker so this workaround won't be necessary.
    const handleRawDateChange = (name: 'startDate' | 'endDate') => (e?: any) => {
        const text = e?.target?.value;
        if (!text) {
            // Field cleared — tell RHF the value is empty so dirty tracking can
            // detect revert-to-initial (matches '' default in useNotificationForm).
            setFieldDirty(name, '');
            return;
        }
        const parsed = moment(text, 'MM/DD/YYYY', true);
        if (parsed.isValid()) setFieldDirty(name, parsed.toDate());
    };

    return (
        <FormProvider {...formApi}>
            <div style={mainContainerStyle}>
                <div style={{ marginBottom: '10px' }}>
                    <h5>Notifications will be posted to Open Chat</h5>

                    <CheckboxFormFieldComponent
                        name={'active'}
                        label={'Active'}
                        reverseOrientation={true}
                        style={null}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFieldDirty('active', e.target.checked)}
                    />
                </div>
                <div style={formContainerStyle}>
                    <div>
                        <TextAreaFormFieldComponent
                            name={'notificationText'}
                            label={'Notification Content'}
                            style={vivliPrivateFieldStyle}
                        />

                        <div style={{ paddingTop: '20px' }}>
                            <div style={textStyle}>
                                Previous message was posted on:
                                <div style={dateText}>{firstAndLastDates[0] ? firstAndLastDates[0] : lastSentMessageState}</div>
                            </div>
                            <div style={textStyle}>
                                Next message scheduled for:
                                <div style={dateText}>{firstAndLastDates[1] ? firstAndLastDates[1] : 'N/A'}</div>
                            </div>
                        </div>

                        <div></div>
                    </div>

                    <div style={rightSectionStyle}>
                        <DatePickerFormFieldComponent
                            name={'startDate'}
                            label={'Start Date'}
                            optClassName="vivliPrivateDateFieldStyle"
                            style={vivliPrivateFieldStyle}
                            dateFormat="MM/dd/yyyy"
                            onChange={(date: Date) => setFieldDirty('startDate', date)}
                            onChangeRaw={handleRawDateChange('startDate')}
                        />

                        <TextFormFieldComponent name={'notificationFrequency'} label={'Frequency (Days)'} style={vivliPrivateFieldStyle} />

                        <DatePickerFormFieldComponent
                            name={'endDate'}
                            label={'End Date/ Next Checkin'}
                            optClassName="vivliPrivateDateFieldStyle"
                            style={vivliPrivateFieldStyle}
                            dateFormat="MM/dd/yyyy"
                            onChange={(date: Date) => setFieldDirty('endDate', date)}
                            onChangeRaw={handleRawDateChange('endDate')}
                        />
                    </div>
                </div>
            </div>
        </FormProvider>
    );
};
